/**
 * Game landing-page helpers
 * @module App/Helpers/gameLpHandlers
 */

import { getAddLeadCountParams } from 'App/Helpers/createLeadRequestHelpers';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

import { upper } from './gameLpDefaultsHandler';
import { getStorageValue } from './localStorageHandler';
import { getUrlParamValue } from './urlHandlers';

/**
 * Filters game-related `key: value` pairs from Sharpstar config for lead attributes.
 * Keeps keys prefixed with `game-` or `build-`, excluding keys that contain `addressables`.
 *
 * @param {Object<string, *>} inputObj - Raw offer/content object from Sharpstar
 * @returns {Object<string, string>} Subset of entries to attach as lead data
 */
export const filterGameRelatedAttributes = (inputObj) => {
    const outputObj = {};

    for (const key in inputObj) {
        if (
            (key.startsWith('game-') || key.startsWith('build-')) &&
            !key.includes('addressables')
        ) {
            outputObj[key] = inputObj[key];
        }
    }

    return outputObj;
};

/**
 * Strips known `game-` / popup prefixes and converts kebab-case segments to PascalCase.
 * "game-shot-streak" -> "ShotStreak"
 *
 * @param {string} key
 * @returns {string}
 */
const normalizeKey = (key) =>
    key
        .replace(/^(game-popup-|game-shot-streak-|game-)/, '')
        .replace(/(^|-)([a-z])/g, (_, __, letter) => upper(letter));

/**
 * Maps Sharpstar `getOffers` content into structured game-init fields: popups, shot streak,
 * generic `game-*` keys (PascalCase), `video:*`, build version/iteration, and GA lists.
 *
 * @param {Object<string, *>} apiConfig - Dynamic config object from the Sharpstar `getOffers` API
 * @returns {Object<string, *>} Normalized shape including optional `gameOfferSettings`,
 *   `gameShotStreakConfiguration`, `videos`, `buildVersion`, `buildIteration`, `currencies`, `itemTypes`
 */
export const getGameInitValues = (apiConfig) => {
    return Object.entries(apiConfig).reduce((result, [key, value]) => {
        if (key.startsWith('game-popup-')) {
            const normalizedPopupKey = normalizeKey(key);

            result.gameOfferSettings = {
                ...(result.gameOfferSettings ?? {}),
                [normalizedPopupKey]: value,
            };
        } else if (key.startsWith('game-shot-streak-')) {
            const normalizedPopupKey = normalizeKey(key);

            result.gameShotStreakConfiguration = {
                ...(result.gameShotStreakConfiguration ?? {}),
                [normalizedPopupKey]: value,
            };
        } else if (key === 'game-silence-logs') {
            result.silenceLogs = value;
        } else if (key.startsWith('game-')) {
            const normalizedKey = normalizeKey(key);

            result[normalizedKey] = value;
        } else if (key.startsWith('video:')) {
            result.videos = {
                ...(result.videos ?? {}),
                [key.slice('video:'.length)]: value,
            };
        } else if (key === 'build-version') {
            result.buildVersion = value;
        } else if (key === 'build-iteration') {
            result.buildIteration = value;
        } else if (key === 'ga-currencies') {
            result.currencies = value?.split(',');
        } else if (key === 'ga-item-types') {
            result.itemTypes = value?.split(',');
        }

        return result;
    }, {});
};

/**
 * Resolves the game build version and iteration, optionally overridden by URL parameters.
 *
 * URL parameters (if present) always take precedence over config values:
 * - `build` defines the base build version
 * - `iteration` is applied ONLY when `build` comes from the URL
 *
 * Build iteration sources are never mixed:
 * - URL build → URL iteration only
 * - Config build → config iteration only
 *
 * @param {string} buildVersion - Base build version from configuration (used when URL param is not provided)
 * @param {string|number|null} buildIteration - Optional build iteration from configuration
 *
 * @returns {{ buildPathAddition: string, isBuildFromUrl: boolean, baseBuild: string }}
 *   baseBuild           Returns game build from url if it exists
 *   buildPathAddition   Resolved build path string in the format `version[/iteration]`
 *   isBuildFromUrl      Indicates whether the build was resolved from URL parameters
 */
export const getGameBuildVersion = (buildVersion, buildIteration) => {
    const urlBuild = getUrlParamValue('build');
    const urlBuildIteration = getUrlParamValue('iteration');

    const isBuildFromUrl = urlBuild != null;
    const baseBuild = isBuildFromUrl ? urlBuild : buildVersion;
    const iteration = isBuildFromUrl
        ? (urlBuildIteration ?? null)
        : (buildIteration ?? null);

    const buildPathAddition = iteration
        ? `${baseBuild}/${iteration}`
        : baseBuild;

    return { baseBuild, buildPathAddition, isBuildFromUrl };
};

/**
 * Returns a `Cache-Control` directive hint for Unity/WebGL asset URLs and media.
 *
 * @param {string} url - Request URL (path/extension inspected)
 * @returns {'must-revalidate'|'immutable'|'no-store'}
 */
export const handleCacheControl = (url) => {
    if (url.match(/\.data/) || url.match(/\.bundle/)) {
        return 'must-revalidate';
    }
    if (url.match(/\.mp4/) || url.match(/\.wav/)) {
        return 'immutable';
    }
    return 'no-store';
};

/**
 * Reads a numeric phase/view counter from nested storage, defaulting to `0` when missing or invalid.
 *
 * @param {string} storageKey
 * @param {string} keyPath
 * @returns {number}
 */
export const getPhaseViews = (storageKey, keyPath) => {
    const raw = getStorageValue(storageKey, keyPath);

    if (!raw) {
        return 0;
    } else {
        const number = parseInt(raw, 10);

        return Number.isFinite(number) ? number : 0;
    }
};

export const sendOneSignalLeadCount = ({
    campaignId,
    storageKey,
    onesignalId,
    urlParams,
    botInfo,
    sharpstarService,
}) => {
    try {
        const requestParams = getAddLeadCountParams({
            campaignId,
            storageKey,
            onesignalId,
            urlParams,
            botInfo,
        });

        sharpstarService.addOneSignalLeadCountTest(requestParams);
    } catch (error) {
        captureCustomException(error, 'AddOneSignalLeadCountTest Failed');
    }
};
